import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useEditionAwareness } from '../../../services/edition-awareness-context';
import { isProductOneOf } from '../../../services/is-product-one-of';
import { getHamsKey } from '../../../services/product-to-hams-key';
import { ModalEntryPoint } from '../shared/modal-entry-point';
import { DIALOG_CONFIG } from './dialog-config';
import { entryPoint } from './standardToPremiumModal.entrypoint';

export function StandardToPremiumModal(): React.JSX.Element {
	const { formatMessage } = useIntl();
	const { product, cloudId, entitlementId, isSinglePaidApp } = useEditionAwareness();
	const selectedContentRef = useRef<'premium' | 'twc'>('premium');
	const [, setSelectedContent] = useState<'premium' | 'twc'>('premium');
	const shouldCheckPaidToTwc = Boolean(
		entitlementId &&
		isSinglePaidApp &&
		isProductOneOf(product, ['jira', 'confluence']) &&
		fg('standard-to-twc-upsell'),
	);

	const entryPointParams = useMemo(
		() => ({
			cloudId,
			hamsProductKey: getHamsKey(product),
			shouldCheckPaidToTwc,
			context: shouldCheckPaidToTwc
				? {
						tenantId: cloudId,
						useCase: 'bundleableEntitlements',
						custom: { entitlementIds: [entitlementId] },
					}
				: null,
		}),
		[cloudId, entitlementId, product, shouldCheckPaidToTwc],
	);
	const onContentSelectionChange = useCallback((selection: 'premium' | 'twc') => {
		selectedContentRef.current = selection;
		setSelectedContent(selection);
	}, []);
	const onClose = useCallback(
		() => onContentSelectionChange('premium'),
		[onContentSelectionChange],
	);
	const entryPointProps = useMemo(
		() => ({ onClose, onContentSelectionChange }),
		[onClose, onContentSelectionChange],
	);

	// ModalTrigger captures this object when the dialog opens. Keep it stable while the
	// dependent Commerce request resolves, and read the latest latched content via getters.
	const modalProps = useMemo(() => {
		const twcModalLabel = formatMessage({
			id: '@atlassian/edition-awareness.dialog.standard-to-premium.label',
			defaultMessage: 'Upgrade plan comparison',
			description:
				'Accessible name for the plan comparison dialog while its final content is selected.',
		});
		const isTwcSelected = () => shouldCheckPaidToTwc && selectedContentRef.current === 'twc';

		return {
			get width() {
				return isTwcSelected() ? '1116px' : 968;
			},
			get height() {
				return isTwcSelected() ? '733px' : undefined;
			},
			get shouldScrollInViewport() {
				return isTwcSelected() ? false : undefined;
			},
			get shouldCloseOnEscape() {
				return isTwcSelected() ? true : undefined;
			},
			get label() {
				return isTwcSelected() ? twcModalLabel : undefined;
			},
		};
	}, [formatMessage, shouldCheckPaidToTwc]);

	return (
		<ModalEntryPoint
			modalProps={modalProps}
			entryPoint={entryPoint}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			dialogConfig={DIALOG_CONFIG}
		/>
	);
}
